'use client';

import React, { useEffect, useRef, useState } from 'react';
//import { useRouter } from 'next/navigation';
import { Container } from 'reactstrap';

//Import components
import DownloadBanner from '@/components/Banners/DownloadStarted/DownloadStarted';
import SingleItemLarge from '@/components/Grids/SingleItemLarge/SingleItemLarge';
import CloselyRelated from '@/components/Grids/CloselyRelated/CloselyRelated';
import FindMore from '@/components/Grids/FindMore/FindMore';
import BannerBlur from '@/components/Banners/BannerBlur/BannerBlur';
import ErrorBoundaryCheck from '@/components/ErrorBoundaryClass/ErrorBoundaryClass';

// Import lib files
import FulfillmentPopup from '@/libs/fulfillmentPopup/fulfillmentPopup';
import { pushGTMDisplayTags } from '@/libs/pushGTMTags/pushGTMTags';

// Context
import { useResourceContext } from '@/context/resourceContext';
import { AssetProps } from '@/libs/interfaceProps/assetProps';
import { ParamsProps } from '@/libs/interfaceProps/paramsProps';
import Skeleton from './fulfillmentSkeleton';
import { useEnvContext } from '@/context/envContext';
import { useLangContext } from '@/context/langContext';
import useResFallbackImages from '@/libs/resFallbackImages/resFallbackImages';
import { sitenameImageIndexHandler } from '@/libs/sitenameImageIndexHandler/sitenameImageIndexHandler';
interface FulfillmentProps {
  userEmailData?: {
    email?: string | false;
    accessCodeGA4: string | undefined;
  };
  params: ParamsProps;
  resourceData?: AssetProps;
  docUrl?: string;
  relatedAssets?: Array<AssetProps>;
  globalEnvConfig: any;
  queryParams: string;
}
const Fulfillment = ({
  userEmailData,
  params,
  resourceData,
  docUrl,
  relatedAssets,
  globalEnvConfig,
  queryParams
}: FulfillmentProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    setResource,
    setDocUrl,
    setRelatedAssets
  } = useResourceContext();
  const {
    setCdnEnv,
    setWebUrl,
    setUserUrl,
    setChApi
  } = useEnvContext();
  const {
    setLang
  } = useLangContext();
  const openedNewTabRef = useRef(false);
  const resFallbackImages = useResFallbackImages();
  const sitenameImageIndex: Record<string, number> = {};
  const resFallbackImage = sitenameImageIndexHandler(sitenameImageIndex, resFallbackImages, resourceData?.primarySiteName ? resourceData?.primarySiteName : 'genericMulti', params?.resId);
  useEffect(() => {
    setRelatedAssets(relatedAssets);
    setResource(resourceData);
    setDocUrl(docUrl);
    setIsLoading(false);
    setCdnEnv(globalEnvConfig?.NEXT_PUBLIC_CDN_ENV);
    setWebUrl(globalEnvConfig?.NEXT_PUBLIC_WEB_URL);
    setUserUrl(globalEnvConfig?.NEXT_PUBLIC_USER_TT_URL);
    setChApi(globalEnvConfig?.NEXT_PUBLIC_CH_API);
    setLang(params.lang);
  }, [setRelatedAssets, relatedAssets, setResource, resourceData, setDocUrl, docUrl, params.lang, globalEnvConfig?.NEXT_PUBLIC_CDN_ENV, globalEnvConfig?.NEXT_PUBLIC_CH_API, globalEnvConfig?.NEXT_PUBLIC_USER_TT_URL, globalEnvConfig?.NEXT_PUBLIC_WEB_URL, setCdnEnv, setLang, setWebUrl, setUserUrl, setChApi]);

  // Enabled the auto popup for top picks items.
  useEffect(() => {
    // Check if popup has already opened. If it
    if (!openedNewTabRef?.current && docUrl) {
      FulfillmentPopup(docUrl);
      openedNewTabRef.current = true;
    }
  }, [docUrl]);
  const userStatus = userEmailData?.email ? 'SITE_MEMBER' : 'NOT_MEMBER';

  // This useEfect runs twice; prevent this using useRef
  // This may not be required in production environments  if this is caused by
  // React 'StrictMode' which doesn't effect production, remove this before eng deployment to test.
  const hasRun = React.useRef(false);
  useEffect(() => {
    if (!hasRun.current) {
      pushGTMDisplayTags({
        appCode: "99",
        contentID: params.resId,
        contentType: resourceData?.docType,
        pageType: 'fulfillment',
        memberStatus: userStatus,
        accessStatus: userEmailData?.accessCodeGA4,
        // how user accessed - fastpass | passwordless | registration non member 
        assetType: 'unsponsored',
        orgID: resourceData?.orgId,
        orgName: resourceData?.orgName
      });
      hasRun.current = true;
    }
  });
  if (isLoading) return <Skeleton />;
  return <ErrorBoundaryCheck data-sentry-element="ErrorBoundaryCheck" data-sentry-component="Fulfillment" data-sentry-source-file="fulfilment.tsx">
   <BannerBlur resId={params?.resId} primarySiteName={resourceData?.primarySiteName ? resourceData?.primarySiteName : 'genericMulti'} fallBackImage={resFallbackImage} data-sentry-element="BannerBlur" data-sentry-source-file="fulfilment.tsx" />

   <div className={'fulfillment-panel'}>
    <DownloadBanner params={params} queryParams={queryParams} data-sentry-element="DownloadBanner" data-sentry-source-file="fulfilment.tsx" />

    <Container data-sentry-element="Container" data-sentry-source-file="fulfilment.tsx">
     <SingleItemLarge params={params} queryParams={queryParams} data-sentry-element="SingleItemLarge" data-sentry-source-file="fulfilment.tsx" />
     <CloselyRelated params={params} queryParams={queryParams} data-sentry-element="CloselyRelated" data-sentry-source-file="fulfilment.tsx" />
     <FindMore params={params} queryParams={queryParams} data-sentry-element="FindMore" data-sentry-source-file="fulfilment.tsx" />
    </Container>
   </div>
  </ErrorBoundaryCheck>;
};
export default Fulfillment;